/**
 * ============================================================================
 * ENTITY DASHBOARD — v5
 * Multi-entités (principale + secondaires), vues métier (entité / CAF / designer / logistique)
 * Structure en onglets : Vue d'ensemble · CA & objectifs · Objectifs (manager) · Mon planning
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo, useId, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageLoader from './PageLoader';
import axios from 'axios';
import { Line, Bar } from 'react-chartjs-2';
import {
    FolderOpen, CheckCircle, Calendar, Users,
    BarChart3, Truck, Package, PenLine, Euro,
    Search, TrendingUp, Target, LayoutDashboard, CalendarDays,
} from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Tooltip,
    Filler,
} from 'chart.js';
import { POLE_LABELS } from '../utils/dashboardPoles';
import { useMercureRefresh } from '../utils/mercure';
import { getPageCache, setPageCache } from '../utils/pageCache';
import { usePresencePage } from '../utils/presence';
import {
    fmtEuroCommercialDash,
    hydraCaObjectifsToByUserId,
    hydraCaObjectifsAnnuelsToByUserId,
    buildCommercialCaBloc,
    pctVersObjectif,
    pipeBudgetForCommercial,
    sumSignedCaForCommercialMonth,
    sumSignedCaForCommercialYear,
    summarizeAnnualObjectifsFromMap,
    ymKey,
} from '../utils/commercialDashboardCa';
import CaObjectifMensuelPanel from './CaObjectifMensuelPanel';
import CaObjectifsGrid from './CaObjectifsGrid';
import MonPlanningRecapCard from './MonPlanningRecapCard';
import '../styles/EntityDashboard.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Filler);

/** Dégradé vertical canvas réutilisable (clé = id chart pour cache). */
function makeVerticalGradient(ctx, area, from, to) {
    if (!area) return from;
    const g = ctx.createLinearGradient(0, area.bottom, 0, area.top);
    g.addColorStop(0, from);
    g.addColorStop(1, to);
    return g;
}

const fmtEuroChart = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(n) || 0);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const MOIS    = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const MOIS_LONG = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

/** Aligné module Projets (+ compat anciens codes) */
const STATUTS = {
    brouillon:  { label: 'Brouillon',   color: '#94a3b8', bg: '#f1f5f9' },
    en_cours:   { label: 'En cours',    color: '#3b82f6', bg: '#dbeafe' },
    signe:      { label: 'Signé',       color: '#22c55e', bg: '#dcfce7' },
    valide:     { label: 'Signé',       color: '#22c55e', bg: '#dcfce7' },
    abandonne:  { label: 'Abandonné',   color: '#f97316', bg: '#ffedd5' },
    perdu:      { label: 'Perdu',       color: '#ef4444', bg: '#fee2e2' },
    annule:     { label: 'Annulé',      color: '#dc2626', bg: '#fee2e2' },
};

const extractId = (ref) => {
    if (!ref) return null;
    if (typeof ref === 'number') return ref;
    if (typeof ref === 'object') {
        if (ref.id) return ref.id;
        if (ref['@id']) { const m = ref['@id'].match(/\/(\d+)$/); return m ? parseInt(m[1], 10) : null; }
    }
    if (typeof ref === 'string') { const m = ref.match(/\/(\d+)$/); return m ? parseInt(m[1], 10) : null; }
    return null;
};

/** IDs entités accessibles (référence principale + secondaires) */
function getAllowedEntityIds(user) {
    if (!user) return [];
    const ids = [];
    const push = (id) => {
        const n = Number(id);
        if (Number.isFinite(n) && !ids.includes(n)) ids.push(n);
    };
    push(extractId(user.entityReference || user.entity_reference_id));
    (user.entitesSecondaires || []).forEach((e) => push(extractId(e)));
    return ids;
}

/** Options pour le sélecteur entité */
function buildEntityOptions(user) {
    const opts = [];
    const seen = new Set();
    const add = (ref) => {
        const id = extractId(ref);
        if (id == null || seen.has(id)) return;
        seen.add(id);
        opts.push({
            id,
            nom: (typeof ref === 'object' && ref?.nom) ? ref.nom : `Entité #${id}`,
            couleur: (typeof ref === 'object' && ref?.couleur) ? ref.couleur : '#6366f1',
        });
    };
    add(user?.entityReference);
    (user?.entitesSecondaires || []).forEach(add);
    return opts.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
}

const fmtMed   = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' }) : '—';

const isUpcoming = (s) => {
    const end = s.demontageFin || s.exploitationFin || s.montageFin || s.montageDebut;
    return end ? new Date(end) >= new Date() : false;
};

const isSigned = (p) => p.statut === 'signe' || p.statut === 'valide';

/** Libellé affichable pour une référence User (IRI, id, ou objet embarqué). */
function userRefLabel(ref, membersById, fallbackPrefix) {
    const id = extractId(ref);
    if (id == null) return 'Non assigné';
    const u = membersById.get(Number(id));
    if (u) {
        const name = `${u.prenom || ''} ${u.nom || ''}`.trim();
        return name || u.email || `${fallbackPrefix} #${id}`;
    }
    if (ref && typeof ref === 'object') {
        const name = `${ref.prenom || ''} ${ref.nom || ''}`.trim();
        return name || `${fallbackPrefix} #${id}`;
    }
    return `${fallbackPrefix} #${id}`;
}

/** Agrège les projets par utilisateur référencé (CAF, designer, préparateur…). */
function aggregateProjetsByUserRef(projets, getRef, membersById, fallbackPrefix) {
    const map = new Map();
    for (const p of projets) {
        const ref = getRef(p);
        const id = extractId(ref);
        const key = id == null ? '__none__' : String(id);
        if (!map.has(key)) {
            const label = id == null ? 'Non assigné' : userRefLabel(ref, membersById, fallbackPrefix);
            map.set(key, { key, label, total: 0, enCours: 0, signes: 0, brouillons: 0 });
        }
        const row = map.get(key);
        row.total += 1;
        if (p.statut === 'en_cours') row.enCours += 1;
        if (isSigned(p)) row.signes += 1;
        if (p.statut === 'brouillon') row.brouillons += 1;
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
}

function montageSoon90j(p) {
    const iso = p.montageDebut;
    if (!iso) return false;
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return false;
    const now = new Date();
    const limit = new Date(now.getTime() + 90 * 86400000);
    return d >= now && d <= limit;
}

// ─── Sous-composants ──────────────────────────────────────────────────────────

const KpiCard = ({ icon: Icon, label, value, sub, color }) => (
    <div className="ed-kpi">
        <div className="ed-kpi-top">
            <span className="ed-kpi-icon" style={{ color }}><Icon size={13} strokeWidth={2.5} /></span>
            <span className="ed-kpi-label">{label}</span>
        </div>
        <p className="ed-kpi-value" style={{ color }}>{value}</p>
        {sub && <p className="ed-kpi-sub">{sub}</p>}
    </div>
);

const Badge = ({ statut }) => {
    const s = STATUTS[statut] || { label: statut, color: '#64748b', bg: '#f1f5f9' };
    return <span className="ed-badge" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
};

/**
 * Objectif mensuel / annuel (vue CAF seul), style demi-jauge centrée avec pourcentage.
 */
const CommercialObjectiveSemiGauge = ({ realised, objective }) => {
    const gid = useId().replace(/:/g, '');
    const denom = objective != null && objective > 0 ? Number(objective) : null;
    const rEuro = Number(realised) || 0;
    const pctRaw = denom == null ? null : (rEuro / denom) * 100;
    const pctFill = pctRaw == null ? 0 : Math.min(100, pctRaw);
    const pctLabel = pctRaw == null ? '—' : `${Math.round(pctRaw)}\u202F%`;
    // Couleur selon la performance
    let cFrom = '#94a3b8';
    let cTo = '#cbd5e1';
    if (pctRaw != null) {
        if (pctRaw >= 100) { cFrom = '#16a34a'; cTo = '#4ade80'; }
        else if (pctRaw >= 70) { cFrom = '#22c55e'; cTo = '#86efac'; }
        else if (pctRaw >= 40) { cFrom = '#f59e0b'; cTo = '#fcd34d'; }
        else { cFrom = '#ef4444'; cTo = '#fca5a5'; }
    }
    const r = 44;
    const cx = 72;
    const cy = 72;
    const arcD = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
    const arcLen = Math.PI * r;
    const dashFilled = (pctFill / 100) * arcLen;
    const ariaLabel = denom == null
        ? 'Progression vers l’objectif : aucun objectif défini'
        : `Progression vers l’objectif : ${Math.round(pctRaw)} pour cent du réalisé par rapport à l’objectif`;

    return (
        <div
            className="ed-ca-gauge-wrap"
            role="img"
            aria-label={ariaLabel}
        >
            <svg
                className="ed-ca-gauge-svg"
                viewBox="0 0 144 92"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <linearGradient id={`gauge-${gid}`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={cFrom} />
                        <stop offset="100%" stopColor={cTo} />
                    </linearGradient>
                </defs>
                <path
                    className="ed-ca-gauge-track"
                    d={arcD}
                    fill="none"
                    strokeLinecap="round"
                    strokeWidth="11"
                />
                {denom != null && pctFill > 0 && (
                    <path
                        className="ed-ca-gauge-fill"
                        d={arcD}
                        fill="none"
                        stroke={`url(#gauge-${gid})`}
                        strokeLinecap="round"
                        strokeWidth="11"
                        strokeDasharray={`${dashFilled} ${arcLen}`}
                    />
                )}
            </svg>
            <span className="ed-ca-gauge-pct" aria-hidden="true" style={pctRaw != null ? { color: cFrom } : undefined}>{pctLabel}</span>
        </div>
    );
};

/** Pastille + jauge pour la vue responsable commercial (lisible en un coup d’œil). */
function encadrementCaHealth(realiseEuros, objectifEuros) {
    const r = Number(realiseEuros) || 0;
    const o = objectifEuros != null && Number(objectifEuros) > 0 ? Number(objectifEuros) : null;
    if (o == null) {
        return { tone: 'none', pct: null, label: 'Sans objectif' };
    }
    const rawPct = (r / o) * 100;
    const pct = Math.round(rawPct * 10) / 10;
    let tone = 'low';
    if (pct >= 100) tone = 'over';
    else if (pct >= 80) tone = 'high';
    else if (pct >= 50) tone = 'mid';
    else tone = 'low';
    let label = 'Sous les objectifs';
    if (pct >= 100) label = pct >= 115 ? 'Très au-dessus' : 'Objectif atteint';
    else if (pct >= 80) label = 'Bon rythme';
    else if (pct >= 50) label = 'À accélérer';
    return { tone, pct, label };
}

const EncadrementCaParlantTd = ({
    realise,
    objectif,
    hint,
}) => {
    const r = realise != null ? Number(realise) : 0;
    const o = objectif != null && Number.isFinite(Number(objectif)) && Number(objectif) > 0 ? Number(objectif) : null;
    const { tone, pct, label } = encadrementCaHealth(r, o);
    let barPct = o != null && pct != null ? Math.min(100, pct) : 0;
    if (barPct > 0 && barPct < 2) barPct = 2;

    return (
        <td className="ed-td-enc-parlant">
            <div className="ed-enc-parlant-inner">
                <p className="ed-enc-parlant-line">
                    <span className="ed-enc-parlant-real">{fmtEuroCommercialDash(r)}</span>
                    <span className="ed-enc-parlant-sur"> / </span>
                    <span className="ed-enc-parlant-obj">{o != null ? fmtEuroCommercialDash(o) : '—'}</span>
                </p>
                {o != null && (
                    <>
                        <div className="ed-enc-parlant-track" aria-hidden>
                            <div
                                className={`ed-enc-parlant-fill ed-enc-parlant-fill--${tone}`}
                                style={{ width: `${barPct}%` }}
                            />
                        </div>
                        <div className="ed-enc-parlant-foot">
                            {pct != null && (
                                <>
                                    <span className="ed-enc-parlant-pctnum">{pct} %</span>
                                    <span className={`ed-enc-parlant-lbl ed-enc-parlant-lbl--${tone}`}>{label}</span>
                                </>
                            )}
                        </div>
                    </>
                )}
                {o == null && r > 0 && (
                    <p className="ed-enc-parlant-nobj">Sans objectif pour cette période.</p>
                )}
                {hint && <p className="ed-enc-parlant-hint">{hint}</p>}
            </div>
        </td>
    );
};

/** Texte volumétrique brouillons pour la ligne « volume » responsable commercial. */
function brouillonEncSuffix(n) {
    const nb = Number(n) || 0;
    if (nb <= 0) return '';
    return ` · ${nb} brouillon${nb > 1 ? 's' : ''}`;
}

function renderKpiRow({
    pole, projetPole, entityColor, salonsUpcomingLen, upcomingSub, montageSoonCount,
    encadrementActifsCount = 0,
}) {
    const ec       = projetPole.filter((p) => p.statut === 'en_cours').length;
    const signes   = projetPole.filter(isSigned).length;
    const brouillon = projetPole.filter((p) => p.statut === 'brouillon').length;

    switch (pole) {
        case 'encadrement_commercial':
            return (
                <>
                    <KpiCard icon={Users} label="Commerciaux actifs" value={encadrementActifsCount} sub="CAF avec au moins un dossier" color={entityColor} />
                    <KpiCard icon={FolderOpen} label="Total dossiers" value={projetPole.length} sub={`${ec} en cours`} color="#6366f1" />
                    <KpiCard icon={CheckCircle} label="Signés" value={signes} sub={`${brouillon} brouillon${brouillon > 1 ? 's' : ''}`} color="#16a34a" />
                    <KpiCard icon={Calendar} label="Salons à venir" value={salonsUpcomingLen} sub={upcomingSub} color="#8b5cf6" />
                </>
            );
        case 'encadrement_designer':
            return (
                <>
                    <KpiCard icon={Users} label="Designers actifs" value={encadrementActifsCount} sub="Avec au moins un projet" color={entityColor} />
                    <KpiCard icon={PenLine} label="Total projets" value={projetPole.length} sub={`${ec} en cours`} color="#6366f1" />
                    <KpiCard icon={CheckCircle} label="Signés" value={signes} sub={`${brouillon} brouillon${brouillon > 1 ? 's' : ''}`} color="#16a34a" />
                    <KpiCard icon={Calendar} label="Salons à venir" value={salonsUpcomingLen} sub={upcomingSub} color="#8b5cf6" />
                </>
            );
        case 'encadrement_logistique':
            return (
                <>
                    <KpiCard icon={Users} label="Préparateurs actifs" value={encadrementActifsCount} sub="Stand avec préparateur assigné" color={entityColor} />
                    <KpiCard icon={Package} label="Projets avec stand" value={projetPole.length} sub={`${ec} en cours`} color="#6366f1" />
                    <KpiCard icon={Truck} label="Montages sous 90 j." value={montageSoonCount} sub="Dates projet / stand" color="#0ea5e9" />
                    <KpiCard icon={Calendar} label="Salons à venir" value={salonsUpcomingLen} sub={upcomingSub} color="#8b5cf6" />
                </>
            );
        case 'commercial':
            return (
                <>
                    <KpiCard icon={FolderOpen} label="Mes dossiers (CAF)" value={projetPole.length} sub={`${ec} en cours`} color={entityColor} />
                    <KpiCard icon={CheckCircle} label="Signés (mes dossiers)" value={signes} sub={`${brouillon} brouillon${brouillon > 1 ? 's' : ''}`} color="#16a34a" />
                    <KpiCard icon={Calendar} label="Salons à venir" value={salonsUpcomingLen} sub={upcomingSub} color="#8b5cf6" />
                </>
            );
        case 'designer':
            return (
                <>
                    <KpiCard icon={PenLine} label="Mes projets (designer)" value={projetPole.length} sub={`${ec} en cours`} color={entityColor} />
                    <KpiCard icon={CheckCircle} label="Signés" value={signes} sub={`${brouillon} brouillon${brouillon > 1 ? 's' : ''}`} color="#16a34a" />
                    <KpiCard icon={Calendar} label="Salons à venir" value={salonsUpcomingLen} sub={upcomingSub} color="#8b5cf6" />
                </>
            );
        case 'logistique':
            return (
                <>
                    <KpiCard icon={Package} label="Projets avec stand" value={projetPole.length} sub="Sur cette entité" color={entityColor} />
                    <KpiCard icon={Truck} label="Montages sous 90 j." value={montageSoonCount} sub="Dates projet / stand" color="#0ea5e9" />
                    <KpiCard icon={Calendar} label="Salons à venir" value={salonsUpcomingLen} sub={upcomingSub} color="#8b5cf6" />
                </>
            );
        case 'entite':
        default:
            return (
                <>
                    <KpiCard icon={FolderOpen} label="Total projets" value={projetPole.length} sub={`${ec} en cours`} color={entityColor} />
                    <KpiCard icon={CheckCircle} label="Signés" value={signes} sub={`${brouillon} brouillon${brouillon > 1 ? 's' : ''}`} color="#16a34a" />
                    <KpiCard icon={Calendar} label="Salons à venir" value={salonsUpcomingLen} sub={upcomingSub} color="#8b5cf6" />
                </>
            );
    }
}

/**
 * Section « CA vs objectifs » (vue commercial + onglet « Mon activité » du manager).
 * Affiche : mois en cours, année (objectif annuel saisi), pipe chiffré, détail mois par mois.
 */
const CommercialCaSection = ({ bloc, year }) => {
    if (!bloc) return null;
    const realiseData = bloc.monthly.map((m) => Math.round(Number(m.realise) || 0));
    // Objectif mensuel : valeur saisie sinon répartition de l'objectif annuel (annuel ÷ 12) → ligne de cible continue.
    const objAvg = (bloc.objAnnuel != null && bloc.objAnnuel > 0) ? bloc.objAnnuel / 12 : null;
    const objectifData = bloc.monthly.map((m) => (
        m.objectif != null ? Math.round(Number(m.objectif)) : (objAvg != null ? Math.round(objAvg) : null)
    ));
    const hasMonthlyData = realiseData.some((v) => v > 0) || objectifData.some((v) => v != null && v > 0);
    const maxVal = Math.max(...realiseData, ...objectifData.map((v) => v || 0), 0);
    const monthlyChartData = {
        labels: MOIS,
        datasets: [
            {
                type: 'bar',
                label: 'Réalisé',
                data: realiseData,
                backgroundColor: (c) => makeVerticalGradient(c.chart.ctx, c.chart.chartArea, 'rgba(129,140,248,0.35)', 'rgba(79,70,229,0.95)'),
                hoverBackgroundColor: (c) => makeVerticalGradient(c.chart.ctx, c.chart.chartArea, 'rgba(129,140,248,0.55)', 'rgba(67,56,202,1)'),
                borderRadius: 7,
                borderSkipped: false,
                maxBarThickness: 30,
                order: 2,
            },
            {
                type: 'line',
                label: 'Objectif',
                data: objectifData,
                borderColor: '#f59e0b',
                borderWidth: 2.5,
                borderDash: [5, 4],
                pointRadius: 0,
                pointHoverRadius: 5,
                pointBackgroundColor: '#f59e0b',
                tension: 0.25,
                spanGaps: true,
                order: 1,
            },
        ],
    };
    const monthlyChartOpts = {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: 'easeOutQuart' },
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: 'circle', padding: 14, font: { size: 11 }, color: '#64748b' },
            },
            tooltip: {
                backgroundColor: 'rgba(15,23,42,0.94)',
                titleColor: '#f8fafc',
                bodyColor: '#e2e8f0',
                padding: 11,
                cornerRadius: 9,
                displayColors: true,
                usePointStyle: true,
                callbacks: {
                    title: (items) => MOIS_LONG[items[0].dataIndex] || MOIS[items[0].dataIndex],
                    label: (i) => (i.raw == null ? `${i.dataset.label} : —` : ` ${i.dataset.label} : ${fmtEuroChart(i.raw)}`),
                    afterBody: (items) => {
                        const idx = items[0].dataIndex;
                        const r = realiseData[idx] || 0;
                        const o = objectifData[idx];
                        if (o == null || o <= 0) return '';
                        return `\nAtteinte : ${Math.round((r / o) * 100)} %`;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                suggestedMax: maxVal > 0 ? maxVal * 1.15 : 1000,
                ticks: {
                    color: '#94a3b8',
                    font: { size: 10 },
                    maxTicksLimit: 5,
                    callback: (v) => new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 }).format(v),
                },
                grid: { color: 'rgba(148,163,184,0.16)', drawBorder: false },
            },
            x: {
                ticks: { color: '#94a3b8', font: { size: 10 } },
                grid: { display: false, drawBorder: false },
            },
        },
    };
    return (
        <section className="ed-commercial-ca" aria-label="Chiffre d'affaires vs objectifs">
            <div className="ed-commercial-ca-cards">
                <div className="ed-ca-slot">
                    <p className="ed-ca-slot-label">{bloc.monthLabel}</p>
                    <div className="ed-ca-slot-figures">
                        <span className="ed-ca-slot-real">{fmtEuroCommercialDash(bloc.realiseMois)}</span>
                        <span className="ed-ca-slot-sep">/</span>
                        <span className="ed-ca-slot-target">{bloc.objectifMois != null ? fmtEuroCommercialDash(bloc.objectifMois) : '—'}</span>
                    </div>
                    <CommercialObjectiveSemiGauge realised={bloc.realiseMois} objective={bloc.objectifMois} />
                    <p className="ed-ca-slot-meta">
                        {bloc.pctMois != null
                            ? `${bloc.pctMois} % de l'objectif mensuel`
                            : bloc.objectifMois == null ? "Pas d'objectif pour ce mois" : ''}
                    </p>
                </div>
                <div className="ed-ca-slot">
                    <p className="ed-ca-slot-label">Année {year}</p>
                    <div className="ed-ca-slot-figures">
                        <span className="ed-ca-slot-real">{fmtEuroCommercialDash(bloc.realiseAnnee)}</span>
                        <span className="ed-ca-slot-sep">/</span>
                        <span className="ed-ca-slot-target">
                            {bloc.objectifAnneeRef != null ? fmtEuroCommercialDash(bloc.objectifAnneeRef) : '—'}
                        </span>
                    </div>
                    <CommercialObjectiveSemiGauge realised={bloc.realiseAnnee} objective={bloc.objectifAnneeRef} />
                    <p className="ed-ca-slot-meta">
                        {bloc.objAnnuel != null
                            ? 'Objectif annuel fixé'
                            : bloc.objectifAnneeMonths > 0
                                ? `Somme de ${bloc.objectifAnneeMonths} objectif${bloc.objectifAnneeMonths > 1 ? 's' : ''} mensuel${bloc.objectifAnneeMonths > 1 ? 's' : ''}`
                                : 'Aucun objectif défini cette année'}
                        {bloc.pctAnnee != null ? ` · ${bloc.pctAnnee} %` : ''}
                    </p>
                </div>
                <div className="ed-ca-slot ed-ca-slot--pipe">
                    <p className="ed-ca-slot-label"><TrendingUp size={12} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 4 }} />Mon pipe</p>
                    <div className="ed-ca-slot-figures">
                        <span className="ed-ca-slot-real ed-ca-slot-real--pipe">{fmtEuroCommercialDash(bloc.pipeEuros)}</span>
                    </div>
                    <p className="ed-ca-slot-meta">
                        {bloc.pipeCount} dossier{bloc.pipeCount > 1 ? 's' : ''} en cours (budgets cumulés)
                    </p>
                </div>
            </div>
            <div className="ed-ca-months">
                <p className="ed-ca-months-title">Détail mois par mois — {year}</p>
                {hasMonthlyData ? (
                    <div className="ed-ca-months-chart">
                        <Bar data={monthlyChartData} options={monthlyChartOpts} />
                    </div>
                ) : (
                    <div className="ed-ca-months-empty">
                        <BarChart3 size={26} strokeWidth={1.6} />
                        <p>Aucun CA réalisé ni objectif saisi pour {year}.</p>
                        <span>Les barres apparaîtront dès qu&apos;un dossier est signé ou qu&apos;un objectif est défini.</span>
                    </div>
                )}
            </div>
            <p className="ed-ca-footnote">
                CA réalisé = budgets HT des dossiers signés (rattachés au mois de montage, sinon de création) · Pipe = budgets des dossiers en cours.
            </p>
        </section>
    );
};

// ─── Dashboard ────────────────────────────────────────────────────────────────

const EntityDashboard = ({ dashboardUser, dashboardPole = 'entite' }) => {
    const navigate  = useNavigate();
    usePresencePage('le Tableau de bord');
    const location  = useLocation();
    const [loading, setLoading] = useState(true);
    const [entity, setEntity]     = useState(null);
    const [projets, setProjets]   = useState([]);
    const [salons, setSalons]     = useState([]);
    const [members, setMembers]   = useState([]);
    const [caObjectifsList, setCaObjectifsList] = useState([]);
    const [caObjectifsAnnuelsList, setCaObjectifsAnnuelsList] = useState([]);
    const [caObjModalOpen, setCaObjModalOpen] = useState(false);
    const [caObjModalCommercial, setCaObjModalCommercial] = useState({ id: null, label: '' });
    const [encCommercialSearch, setEncCommercialSearch] = useState('');
    const [activeTab, setActiveTab] = useState('apercu');

    const [selectedEntityId, setSelectedEntityId] = useState(null);

    const entityOptions = useMemo(() => buildEntityOptions(dashboardUser), [dashboardUser]);
    const allowedEntityIds = useMemo(() => getAllowedEntityIds(dashboardUser), [dashboardUser]);
    const allowedKey = allowedEntityIds.join(',');

    useEffect(() => {
        if (!allowedEntityIds.length) return;
        setSelectedEntityId((prev) => {
            if (prev != null && allowedEntityIds.includes(prev)) return prev;
            const primary = extractId(dashboardUser?.entityReference);
            if (primary != null && allowedEntityIds.includes(primary)) return primary;
            return allowedEntityIds[0];
        });
    }, [allowedKey, dashboardUser]); // eslint-disable-line react-hooks/exhaustive-deps

    const poleLabel = POLE_LABELS[dashboardPole] || POLE_LABELS.entite;
    const selfId = Number(dashboardUser?.id ?? dashboardUser?.userId);
    const peutDefinirObjectifsCa = Boolean(dashboardUser && dashboardUser.fonctionEncadrement === 'responsable_commercial');

    const bootedRef = useRef(false);
    const load = useCallback(async () => {
        if (!dashboardUser) return;
        try {
            const ids = getAllowedEntityIds(dashboardUser);
            if (!ids.length) {
                setEntity(null);
                setProjets([]);
                setSalons([]);
                setMembers([]);
                setCaObjectifsList([]);
                setCaObjectifsAnnuelsList([]);
                setLoading(false);
                bootedRef.current = true;
                return;
            }
            let targetEntityId = selectedEntityId;
            if (targetEntityId == null || !ids.includes(targetEntityId)) {
                targetEntityId = extractId(dashboardUser.entityReference) || ids[0];
            }

            // Cache mémoire : au retour sur la page, affichage immédiat des
            // dernières données (pas de loader), puis resynchro silencieuse.
            const cached = getPageCache('dashboard-page');
            if (!bootedRef.current && cached?.entityId === targetEntityId) {
                setEntity(cached.entity);
                setProjets(cached.projets || []);
                setSalons(cached.salons || []);
                setMembers(cached.members || []);
                setCaObjectifsList(cached.caObjectifs || []);
                setCaObjectifsAnnuelsList(cached.caObjectifsAnnuels || []);
                setLoading(false);
                bootedRef.current = true;
            }
            if (!bootedRef.current) setLoading(true);

            const [eR, pR, sR, uR, oR, oAR] = await Promise.allSettled([
                axios.get(`${API_URL}/entities/${targetEntityId}`, { withCredentials: true, headers: { Accept: 'application/ld+json' } }),
                axios.get(`${API_URL}/projets`, { withCredentials: true, params: { pagination: false }, headers: { Accept: 'application/ld+json' } }),
                axios.get(`${API_URL}/salons`, { withCredentials: true, params: { pagination: false }, headers: { Accept: 'application/ld+json' } }),
                axios.get(`${API_URL}/users`, { withCredentials: true, params: { pagination: false }, headers: { Accept: 'application/ld+json' } }),
                axios.get(`${API_URL}/ca_objectif_mensuels`, {
                    withCredentials: true,
                    params: {
                        pagination: false,
                        entite: `/api/entities/${targetEntityId}`,
                    },
                    headers: { Accept: 'application/ld+json' },
                }),
                axios.get(`${API_URL}/ca_objectif_annuels`, {
                    withCredentials: true,
                    params: {
                        pagination: false,
                        entite: `/api/entities/${targetEntityId}`,
                    },
                    headers: { Accept: 'application/ld+json' },
                }),
            ]);

            const hydraMember = (r) => {
                if (r.status !== 'fulfilled') return [];
                const d = r.value.data;
                return d['hydra:member'] || d.member || [];
            };

            const entityData = eR.status === 'fulfilled' ? eR.value.data : null;
            const allProjets = hydraMember(pR).filter((p) => ids.includes(Number(extractId(p.entite))));
            const forEntity = allProjets.filter((p) => extractId(p.entite) === targetEntityId);
            const salonsList = hydraMember(sR);
            const allUsers   = hydraMember(uR);

            const membersList = allUsers.filter((u) => extractId(u.entityReference || u.entity_reference_id) === targetEntityId);
            const caObjectifs = hydraMember(oR);
            const caObjectifsAnnuels = hydraMember(oAR);

            setEntity(entityData);
            setProjets(forEntity);
            setSalons(salonsList);
            setMembers(membersList);
            setCaObjectifsList(caObjectifs);
            setCaObjectifsAnnuelsList(caObjectifsAnnuels);
            setPageCache('dashboard-page', {
                entityId: targetEntityId, entity: entityData, projets: forEntity,
                salons: salonsList, members: membersList,
                caObjectifs, caObjectifsAnnuels,
            });
        } catch (e) {
            console.error('Dashboard:', e);
        } finally {
            setLoading(false);
            bootedRef.current = true;
        }
    }, [dashboardUser, selectedEntityId]);

    useEffect(() => {
        setEncCommercialSearch('');
    }, [selectedEntityId, dashboardPole]);

    useEffect(() => {
        setActiveTab('apercu');
    }, [dashboardPole]);

    useEffect(() => { load(); }, [load, location.pathname]);

    useEffect(() => {
        const handleVisibility = () => { if (document.visibilityState === 'visible') load(); };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [load]);

    // Temps réel : `load` est déjà silencieux après le premier chargement (bootedRef)
    useMercureRefresh(['projets', 'salons', 'ca_objectif_mensuels', 'ca_objectif_annuels'], load, 2000);

    const projetPole = useMemo(() => {
        let list = projets;
        if (dashboardPole === 'commercial' && Number.isFinite(selfId)) {
            list = projets.filter((p) => extractId(p.caf) === selfId);
        }
        if (dashboardPole === 'designer' && Number.isFinite(selfId)) {
            list = projets.filter((p) => extractId(p.designer) === selfId);
        }
        if (dashboardPole === 'logistique' || dashboardPole === 'encadrement_logistique') {
            list = projets.filter((p) => p.standId);
        }
        return list;
    }, [projets, dashboardPole, selfId]);

    const membersById = useMemo(() => {
        const m = new Map();
        (members || []).forEach((u) => {
            if (u.id != null) m.set(Number(u.id), u);
        });
        return m;
    }, [members]);

    const caObjectifsByCommercialId = useMemo(
        () => hydraCaObjectifsToByUserId(caObjectifsList),
        [caObjectifsList],
    );

    const caObjectifsAnnuelsByCommercialId = useMemo(
        () => hydraCaObjectifsAnnuelsToByUserId(caObjectifsAnnuelsList),
        [caObjectifsAnnuelsList],
    );

    const encadrementBreakdown = useMemo(() => {
        if (dashboardPole === 'encadrement_commercial') {
            return aggregateProjetsByUserRef(projetPole, (p) => p.caf, membersById, 'CAF');
        }
        if (dashboardPole === 'encadrement_designer') {
            return aggregateProjetsByUserRef(projetPole, (p) => p.designer, membersById, 'Designer');
        }
        if (dashboardPole === 'encadrement_logistique') {
            return aggregateProjetsByUserRef(projetPole, (p) => p.preparateur, membersById, 'Prép.');
        }
        return [];
    }, [dashboardPole, projetPole, membersById]);

    const encadrementActifsCount = useMemo(
        () => encadrementBreakdown.filter((r) => r.key !== '__none__').length,
        [encadrementBreakdown],
    );

    const commercialCaBloc = useMemo(() => {
        if (dashboardPole !== 'commercial' || !Number.isFinite(selfId) || selectedEntityId == null) return null;
        const y = new Date().getFullYear();
        const monthMapSelf = caObjectifsByCommercialId[Number(selfId)] || {};
        const annuelSelf = (caObjectifsAnnuelsByCommercialId[Number(selfId)] || {})[y] ?? null;
        return buildCommercialCaBloc({ projetsCaf: projetPole, monthMap: monthMapSelf, objectifAnnuelSaisi: annuelSelf, year: y });
    }, [dashboardPole, selfId, selectedEntityId, projetPole, caObjectifsByCommercialId, caObjectifsAnnuelsByCommercialId]);

    // Onglet « Mon activité » du manager commercial : ses propres dossiers (CAF = lui) + CA/objectifs/pipe.
    const managerSelfProjets = useMemo(() => {
        if (dashboardPole !== 'encadrement_commercial' || !Number.isFinite(selfId)) return [];
        return projets.filter((p) => extractId(p.caf) === selfId);
    }, [dashboardPole, projets, selfId]);

    const managerSelfCaBloc = useMemo(() => {
        if (dashboardPole !== 'encadrement_commercial' || !Number.isFinite(selfId) || selectedEntityId == null) return null;
        const y = new Date().getFullYear();
        const monthMapSelf = caObjectifsByCommercialId[Number(selfId)] || {};
        const annuelSelf = (caObjectifsAnnuelsByCommercialId[Number(selfId)] || {})[y] ?? null;
        return buildCommercialCaBloc({ projetsCaf: managerSelfProjets, monthMap: monthMapSelf, objectifAnnuelSaisi: annuelSelf, year: y });
    }, [dashboardPole, selfId, selectedEntityId, managerSelfProjets, caObjectifsByCommercialId, caObjectifsAnnuelsByCommercialId]);

    // Commerciaux de l'entité pour la grille d'objectifs : pôle commercial + tout CAF déjà actif.
    const commerciauxForGrid = useMemo(() => {
        if (dashboardPole !== 'encadrement_commercial') return [];
        const map = new Map();
        const labelOf = (u) => `${u.prenom || ''} ${u.nom || ''}`.trim() || u.email || `#${u.id}`;
        (members || []).forEach((u) => {
            const cat = u.pole?.categorie || u.pole?.category;
            if (cat === 'commercial' && u.id != null) {
                map.set(Number(u.id), { id: Number(u.id), label: labelOf(u) });
            }
        });
        projetPole.forEach((p) => {
            const id = extractId(p.caf);
            if (id == null || map.has(Number(id))) return;
            const u = membersById.get(Number(id));
            map.set(Number(id), { id: Number(id), label: u ? labelOf(u) : (p.cafNom || `CAF #${id}`) });
        });
        return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'fr'));
    }, [dashboardPole, members, projetPole, membersById]);

    const encadrementCommercialCaRows = useMemo(() => {
        if (dashboardPole !== 'encadrement_commercial') return null;
        const now = new Date();
        const y = now.getFullYear();
        const mIdx = now.getMonth();
        const mk = ymKey(y, mIdx);
        return encadrementBreakdown.map((row) => {
            if (row.key === '__none__') {
                return {
                    row,
                    realiseMois: null,
                    objectifMois: null,
                    pctMois: null,
                    realiseAnnee: null,
                    objectifAnneeSum: 0,
                    objectifAnneeMonths: 0,
                    pctAnnee: null,
                };
            }
            const cid = Number(row.key);
            const projCaf = projetPole.filter((p) => extractId(p.caf) === cid);
            const omap = caObjectifsByCommercialId[cid] || {};
            const objectifMois = omap[mk] != null && Number.isFinite(omap[mk]) ? Number(omap[mk]) : null;
            const realiseMois = sumSignedCaForCommercialMonth(projCaf, y, mIdx);
            const realiseAnnee = sumSignedCaForCommercialYear(projCaf, y);
            const { sum: objectifAnneeSum, count: objectifAnneeMonths } = summarizeAnnualObjectifsFromMap(omap, y);
            const annuelSaisi = (caObjectifsAnnuelsByCommercialId[cid] || {})[y] ?? null;
            const objectifAnneeRef = (annuelSaisi != null && annuelSaisi > 0) ? annuelSaisi : (objectifAnneeSum > 0 ? objectifAnneeSum : null);
            const pctMois = pctVersObjectif(realiseMois, objectifMois);
            const pctAnnee = objectifAnneeRef != null ? pctVersObjectif(realiseAnnee, objectifAnneeRef) : null;
            const pipeEuros = pipeBudgetForCommercial(projCaf);
            return {
                row,
                realiseMois,
                objectifMois,
                pctMois,
                realiseAnnee,
                objectifAnneeSum,
                objectifAnneeMonths,
                annuelSaisi,
                objectifAnneeRef,
                pctAnnee,
                pipeEuros,
            };
        });
    }, [dashboardPole, encadrementBreakdown, projetPole, caObjectifsByCommercialId, caObjectifsAnnuelsByCommercialId]);

    const encCommercialRowsFiltered = useMemo(() => {
        if (!encadrementCommercialCaRows) return null;
        const needle = encCommercialSearch.trim();
        if (!needle) return encadrementCommercialCaRows;
        const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const q = norm(needle);
        return encadrementCommercialCaRows.filter((st) => norm(st.row.label).includes(q));
    }, [encadrementCommercialCaRows, encCommercialSearch]);

    const year = new Date().getFullYear();
    const montageSoonCount = projetPole.filter(montageSoon90j).length;

    const upcoming  = salons.filter(isUpcoming).sort((a,b) => new Date(a.montageDebut||0) - new Date(b.montageDebut||0));
    const next      = upcoming[0] || null;
    const upcomingSub = next ? `Prochain : ${next.nom}` : 'Aucun prévu';

    const recents   = [...projetPole].sort((a,b) => (b.id||0)-(a.id||0)).slice(0, 7);

    const byMonth = Array(12).fill(0);
    projetPole.forEach((p) => {
        const ref = p.montageDebut || p.createdAt;
        if (!ref) return;
        const d = new Date(ref);
        if (d.getFullYear() !== year) return;
        byMonth[d.getMonth()]++;
    });

    let chartTitleSuffix = '';
    if (dashboardPole === 'commercial') chartTitleSuffix = ' — vos dossiers CAF';
    if (dashboardPole === 'designer') chartTitleSuffix = ' — vos projets designer';
    if (dashboardPole === 'logistique') chartTitleSuffix = ' — projets avec stand';
    if (dashboardPole === 'encadrement_commercial') chartTitleSuffix = ' — pilotage commercial (tous CAF)';
    if (dashboardPole === 'encadrement_designer') chartTitleSuffix = ' — pilotage designer (tous designers)';
    if (dashboardPole === 'encadrement_logistique') chartTitleSuffix = ' — pilotage logistique (stands)';

    const encadrementTableTitle = dashboardPole === 'encadrement_commercial'
        ? 'Répartition par commercial (CAF)'
        : dashboardPole === 'encadrement_designer'
            ? 'Répartition par designer'
            : dashboardPole === 'encadrement_logistique'
                ? 'Répartition par préparateur (stands)'
                : '';

    const entityColor = entity?.couleur || '#6366f1';
    const hex2rgb = (hex) => {
        const r = parseInt(hex.slice(1,3),16);
        const g = parseInt(hex.slice(3,5),16);
        const b = parseInt(hex.slice(5,7),16);
        return `${r},${g},${b}`;
    };
    const rgb = entityColor.startsWith('#') ? hex2rgb(entityColor) : '99,102,241';

    const chartData = {
        labels: MOIS,
            datasets: [{
            data: byMonth,
            borderColor: entityColor,
            backgroundColor: (c) => makeVerticalGradient(c.chart.ctx, c.chart.chartArea, `rgba(${rgb},0.02)`, `rgba(${rgb},0.28)`),
                borderWidth: 3,
                fill: true,
            tension: 0.42,
            pointRadius: 0,
            pointHoverRadius: 6,
            pointHitRadius: 16,
            pointBackgroundColor: '#fff',
            pointBorderColor: entityColor,
            pointBorderWidth: 2.5,
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderWidth: 3,
        }],
    };

    const chartOpts = {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 650, easing: 'easeOutQuart' },
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { display: false }, tooltip: {
            backgroundColor: 'rgba(15,23,42,0.94)',
            titleColor: '#f8fafc',
            bodyColor: '#e2e8f0',
            padding: 11,
            cornerRadius: 9,
            displayColors: false,
            callbacks: { title: (i) => MOIS_LONG[i[0].dataIndex] || MOIS[i[0].dataIndex], label: (i) => ` ${i.raw} projet${i.raw > 1 ? 's' : ''}` },
        }},
        scales: {
            y: { beginAtZero: true, ticks: { stepSize: 1, color: '#94a3b8', font: { size: 10 }, maxTicksLimit: 6 }, grid: { color: 'rgba(148,163,184,0.16)', drawBorder: false } },
            x: { ticks: { color: '#94a3b8', font: { size: 10 } }, grid: { display: false, drawBorder: false } },
        },
    };

    // ── Onglets selon le pôle ────────────────────────────────────────────────
    const tabs = useMemo(() => {
        const list = [{
            id: 'apercu',
            label: dashboardPole === 'encadrement_commercial' ? 'Pilotage équipe' : "Vue d'ensemble",
            icon: dashboardPole === 'encadrement_commercial' ? Users : LayoutDashboard,
        }];
        if (dashboardPole === 'commercial') {
            list.push({ id: 'ca', label: 'CA & objectifs', icon: Euro });
        }
        if (dashboardPole === 'encadrement_commercial') {
            list.push({ id: 'perso', label: 'Mon activité', icon: Euro });
            if (peutDefinirObjectifsCa) list.push({ id: 'objectifs', label: 'Objectifs', icon: Target });
        }
        if (Number.isFinite(selfId)) {
            list.push({ id: 'planning', label: 'Mon planning', icon: CalendarDays });
        }
        return list;
    }, [dashboardPole, peutDefinirObjectifsCa, selfId]);

    const kpiRowEl = (
        <div className="ed-kpi-row">
            {renderKpiRow({
                pole: dashboardPole,
                projetPole,
                entityColor,
                salonsUpcomingLen: upcoming.length,
                upcomingSub,
                montageSoonCount,
                encadrementActifsCount,
            })}
        </div>
    );

    const renderEncadrementTableCard = (heroLayout = false) => (
        <div className={`ed-card ed-card--enc-table${heroLayout ? ' ed-card--enc-commercial-hero' : ''}`}>
            <div className="ed-card-hd">
                <span className="ed-card-title"><Users size={13} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 5 }} />{encadrementTableTitle}</span>
                <span className="ed-card-chip">
                    {dashboardPole === 'encadrement_commercial' && encCommercialSearch.trim() && encCommercialRowsFiltered
                        ? `${encCommercialRowsFiltered.length} affiché${encCommercialRowsFiltered.length !== 1 ? 's' : ''} sur ${encadrementBreakdown.length}`
                        : `${encadrementBreakdown.length} ligne${encadrementBreakdown.length > 1 ? 's' : ''}`}
                </span>
            </div>
            {dashboardPole === 'encadrement_commercial' && encadrementBreakdown.length > 0 && (
                <div className="ed-enc-commercial-search">
                    <label htmlFor="ed-enc-commercial-search" className="ed-enc-commercial-search-label">
                        <Search size={14} strokeWidth={2.25} className="ed-enc-commercial-search-ic" aria-hidden />
                        Rechercher
                    </label>
                    <input
                        id="ed-enc-commercial-search"
                        type="search"
                        className="ed-enc-commercial-search-input"
                        placeholder="Prénom, nom du CAF…"
                        value={encCommercialSearch}
                        onChange={(e) => setEncCommercialSearch(e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                    />
                </div>
            )}
            <div className={`ed-enc-table-scroll${dashboardPole === 'encadrement_commercial' ? ' ed-enc-table-scroll--commercial' : ''}`}>
                <table className="ed-table">
                    <thead>
                        <tr>
                            {dashboardPole === 'encadrement_commercial' ? (
                                <>
                                    <th className="ed-th-commercial">Commercial (CAF)</th>
                                    <th className="ed-th-volume">
                                        <span className="ed-th-stack">
                                            <span className="ed-th-main">Charge du pipe</span>
                                            <span className="ed-th-sub">Nombre de dossiers · statuts</span>
                                        </span>
                                    </th>
                                    <th className="ed-th-parlant">
                                        <span className="ed-th-stack">
                                            <span className="ed-th-main">Le mois en cours</span>
                                            <span className="ed-th-sub">Budget signé vs objectif du mois</span>
                                        </span>
                                    </th>
                                    <th className="ed-th-parlant">
                                        <span className="ed-th-stack">
                                            <span className="ed-th-main">Depuis janv. {year}</span>
                                            <span className="ed-th-sub">Cumul signé vs somme des objectifs saisis</span>
                                        </span>
                                    </th>
                                    <th className="ed-th-act">{peutDefinirObjectifsCa ? 'Actions' : ''}</th>
                                </>
                            ) : (
                                <>
                                    <th>Équipier</th>
                                    <th>Projets</th>
                                    <th>En cours</th>
                                    <th>Signés</th>
                                    <th>Brouillons</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {encadrementBreakdown.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="ed-table-empty">Aucune donnée dans cette vue</td>
                            </tr>
                        ) : dashboardPole === 'encadrement_commercial' && encCommercialRowsFiltered ? (
                            encCommercialRowsFiltered.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="ed-table-empty">
                                        Aucun commercial ne correspond à «&nbsp;{encCommercialSearch.trim()}&nbsp;».
                                    </td>
                                </tr>
                            ) : (
                            encCommercialRowsFiltered.map((st) => {
                                const hintPartial = st.objectifAnneeMonths > 0 && st.objectifAnneeMonths < 12;
                                const hintAnneeComparison = hintPartial
                                    ? `Comparaison basée sur ${st.objectifAnneeMonths} mois prévu${st.objectifAnneeMonths > 1 ? 's' : ''} avec objectif (${year}).`
                                    : '';
                                const hintSansObjYear = st.objectifAnneeMonths === 0
                                    ? 'Aucun objectif sur cette année : saisissez au moins un mois pour suivre le cumul.'
                                    : hintAnneeComparison;
        return (
                                    <tr key={st.row.key}>
                                        <td className="ed-table-name ed-td-commercial">{st.row.label}</td>
                                        <td className="ed-td-enc-vol">
                                            <span className="ed-enc-vol-inline">
                                                <span className="ed-enc-vol-n">{st.row.total}</span>
                                                <span className="ed-enc-vol-meta">
                                                    {' '}dossiers — {st.row.enCours} en cours, {st.row.signes} signé{st.row.signes > 1 ? 's' : ''}
                                                    {brouillonEncSuffix(st.row.brouillons)}
                                                </span>
                                            </span>
                                            {st.pipeEuros > 0 && (
                                                <span className="ed-enc-vol-pipe">
                                                    <TrendingUp size={11} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                                                    Pipe&nbsp;: {fmtEuroCommercialDash(st.pipeEuros)}
                                                </span>
                                            )}
                                        </td>
                                        <EncadrementCaParlantTd
                                            realise={st.realiseMois}
                                            objectif={st.objectifMois}
                                        />
                                        <EncadrementCaParlantTd
                                            realise={st.realiseAnnee}
                                            objectif={st.objectifAnneeRef}
                                            hint={st.annuelSaisi != null && st.annuelSaisi > 0
                                                ? 'Objectif annuel fixé.'
                                                : (st.objectifAnneeMonths === 0 ? hintSansObjYear : hintAnneeComparison)}
                                        />
                                        <td className="ed-td-act">
                                            {peutDefinirObjectifsCa && st.row.key !== '__none__' && (
                                                <button
                                                    type="button"
                                                    className="ed-enc-ca-obj-btn"
                                                    onClick={() => {
                                                        setCaObjModalCommercial({
                                                            id: Number(st.row.key),
                                                            label: st.row.label,
                                                        });
                                                        setCaObjModalOpen(true);
                                                    }}
                                                >
                                                    Fixer les objectifs
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                            )
                        ) : (
                            encadrementBreakdown.map((row) => (
                                <tr key={row.key}>
                                    <td className="ed-table-name">{row.label}</td>
                                    <td>{row.total}</td>
                                    <td>{row.enCours}</td>
                                    <td>{row.signes}</td>
                                    <td>{row.brouillons}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>
            </div>
        );

    if (loading) return <PageLoader text="Chargement du tableau de bord…" />;

    if (!entity) {
        return (
            <div className="entity-dashboard">
                <div className="error-container"><p>Aucune entité associée</p></div>
            </div>
        );
    }

    return (
        <div className="entity-dashboard">
            <div className="ed-topbar ed-topbar--with-extra" style={{ borderBottomColor: `${entityColor}44` }}>
                <div className="ed-topbar-left">
                    <span className="ed-topbar-dot" style={{ background: entityColor }} />
                    <h1 className="ed-topbar-title">{entity.nom}</h1>
                    <span className="ed-topbar-sep">·</span>
                    <span className="ed-topbar-sub">{poleLabel}</span>
                    {entityOptions.length > 1 && (
                        <label className="ed-entity-pick-label">
                            <span className="ed-entity-pick-text">Entité</span>
                            <select
                                className="ed-entity-pick-select"
                                value={selectedEntityId ?? ''}
                                onChange={(e) => setSelectedEntityId(Number(e.target.value))}
                            >
                                {entityOptions.map((o) => (
                                    <option key={o.id} value={o.id}>{o.nom}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    </div>
                <p className="ed-topbar-date">
                    {new Date().toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
                </p>
                    </div>

            <div className="ed-shell">

                <div className="ed-tabs" role="tablist" aria-label="Sections du tableau de bord">
                    {tabs.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === id}
                            className={`ed-tab${activeTab === id ? ' active' : ''}`}
                            onClick={() => setActiveTab(id)}
                        >
                            <Icon size={15} strokeWidth={2.25} /> {label}
                        </button>
                    ))}
            </div>

                {activeTab === 'apercu' && (
                    <div className="ed-tabpanel">
                        {kpiRowEl}

                        {dashboardPole === 'encadrement_commercial' && renderEncadrementTableCard(true)}
                        {(dashboardPole === 'encadrement_designer' || dashboardPole === 'encadrement_logistique') && renderEncadrementTableCard(false)}

                        <div className="ed-overview-grid">
                            <div className="ed-col-left">
                                <div className="ed-card">
                                    <div className="ed-card-hd">
                                        <span className="ed-card-title"><BarChart3 size={13} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 5 }} />Projets par mois — {year}{chartTitleSuffix}</span>
                                        <span className="ed-card-chip">{projetPole.length} suivis</span>
                    </div>
                                    <div className="ed-chart-wrap">
                                        <Line data={chartData} options={chartOpts} />
                    </div>
                </div>

                                <div className="ed-card">
                                    <div className="ed-card-hd">
                                        <span className="ed-card-title"><FolderOpen size={13} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 5 }} />Projets récents</span>
                                        <button type="button" className="ed-card-btn" onClick={() => navigate('/admin/projets')}>Voir tout</button>
                    </div>
                                    <table className="ed-table">
                                        <thead>
                                            <tr>
                                                <th>N°</th>
                                                <th>Client / Donneur</th>
                                                <th>Salon</th>
                                                <th>Profil</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recents.length === 0 ? (
                                                <tr><td colSpan={4} className="ed-table-empty">Aucun projet dans cette vue</td></tr>
                                            ) : recents.map((p) => (
                                                <tr key={p.id}>
                                                    <td className="ed-table-erp">{p.numeroErp ? `#${p.numeroErp}` : `—`}</td>
                                                    <td className="ed-table-name">{p.donneurOrdreNom || p.clientNom || `Projet #${p.id}`}</td>
                                                    <td className="ed-table-salon">{p.salonNom || <span className="ed-muted">—</span>}</td>
                                                    <td><Badge statut={p.statut} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                    </div>
                </div>

                            <div className="ed-col-right">
                                {(dashboardPole === 'logistique' || dashboardPole === 'encadrement_logistique') && (
                                    <div className="ed-card ed-card--log-shortcuts">
                                        <div className="ed-card-hd">
                                            <span className="ed-card-title"><Truck size={13} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 5 }} />Accès rapides logistique</span>
                    </div>
                                        <div className="ed-log-shortcuts-inner">
                                            <button type="button" className="ed-log-shortcut-btn" onClick={() => navigate('/admin/preparation')}>Préparation</button>
                                            <button type="button" className="ed-log-shortcut-btn" onClick={() => navigate('/admin/suivi-retours')}>Suivi retours</button>
                                            <button type="button" className="ed-log-shortcut-btn" onClick={() => navigate('/admin/validation-devis')}>Validation devis</button>
                                            <button type="button" className="ed-log-shortcut-btn" onClick={() => navigate('/admin/events')}>Planning</button>
                    </div>
                </div>
                                )}

                                <div className="ed-card">
                                    <div className="ed-card-hd">
                                        <span className="ed-card-title"><Calendar size={13} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 5 }} />Salons à venir</span>
                                        <button type="button" className="ed-card-btn" onClick={() => navigate('/admin/events')}>Voir tout</button>
                    </div>
                                    <div className="ed-salon-list">
                                        {upcoming.length === 0 ? (
                                            <p className="ed-empty">Aucun salon à venir</p>
                                        ) : upcoming.slice(0, 6).map((s, idx) => {
                                            const debut = s.montageDebut || s.exploitationDebut;
                                            const fin   = s.demontageFin || s.exploitationFin;
                                            return (
                                                <div key={s.id} className={`ed-salon-row${idx === 0 ? ' ed-salon-first' : ''}`}>
                                                    <div className="ed-salon-indicator" style={{ background: idx === 0 ? entityColor : '#e2e8f0' }} />
                                                    <div className="ed-salon-info">
                                                        <p className="ed-salon-name">{s.nom}</p>
                                                        <p className="ed-salon-dates">{fmtMed(debut)}{fin && fin !== debut ? `  ${fmtMed(fin)}` : ''}</p>
                                                        {s.lieu && <p className="ed-salon-lieu">{s.lieu}</p>}
                    </div>
                                                    {idx === 0 && <span className="ed-salon-next-tag" style={{ background: `${entityColor}18`, color: entityColor }}>Suivant</span>}
                </div>
                                            );
                                        })}
            </div>
                </div>
                </div>
            </div>
                    </div>
                )}

                {activeTab === 'ca' && (
                    <div className="ed-tabpanel">
                        <CommercialCaSection bloc={commercialCaBloc} year={year} />
                    </div>
                )}

                {activeTab === 'perso' && (
                    <div className="ed-tabpanel">
                        <div className="ed-kpi-row">
                            {renderKpiRow({
                                pole: 'commercial',
                                projetPole: managerSelfProjets,
                                entityColor,
                                salonsUpcomingLen: upcoming.length,
                                upcomingSub,
                                montageSoonCount,
                            })}
                                    </div>
                        <CommercialCaSection bloc={managerSelfCaBloc} year={year} />
                                </div>
                )}

                {activeTab === 'objectifs' && peutDefinirObjectifsCa && (
                    <div className="ed-tabpanel">
                        <CaObjectifsGrid
                            apiUrl={API_URL}
                            entityId={selectedEntityId}
                            commerciaux={commerciauxForGrid}
                            definiParId={Number.isFinite(selfId) ? selfId : null}
                            onSaved={load}
                        />
                    </div>
                )}

                {activeTab === 'planning' && Number.isFinite(selfId) && (
                    <div className="ed-tabpanel">
                        <MonPlanningRecapCard userId={selfId} />
                    </div>
                        )}
                    </div>

            <CaObjectifMensuelPanel
                apiUrl={API_URL}
                open={caObjModalOpen && caObjModalCommercial.id != null && selectedEntityId != null}
                onClose={() => { setCaObjModalOpen(false); setCaObjModalCommercial({ id: null, label: '' }); }}
                entityId={selectedEntityId}
                commercialId={caObjModalCommercial.id}
                commercialLabel={caObjModalCommercial.label || ''}
                allRowsForUpsertSearch={caObjectifsList}
                defaultMoisRef={ymKey(new Date().getFullYear(), new Date().getMonth())}
                onSaved={load}
            />
        </div>
    );
};

export default EntityDashboard;
